import type { ReactElement, ReactNode } from "react";
import { useState } from "react";
import type { DroppableProvided, DropResult } from "react-beautiful-dnd";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import type { NskResponse } from "~/models/Api";
import type { DealBillingContext } from "~/models/DealBilling";
import type {
	DealSettlement,
	DealSettlementEdit
} from "~/models/DealSettlement";
import { SettlementListFooterRow } from "./SettlementListFooterRow";
import { SettlementListRestorationAmountRow } from "./SettlementListRestorationAmountRow";
import { SettlementListRow } from "./SettlementListRow";

type Props = {
	constructionContext: DealBillingContext;
	handleDestroy: (deleteSettlement: DealSettlement) => Promise<NskResponse>;
	handleMutateSettlements: () => Promise<void>;
	handleUpdate: (
		submitValue: DealSettlementEdit,
		billingId: number
	) => Promise<NskResponse>;
	handleUpdateSeq: (settlements: DealSettlement[]) => Promise<void>;
	onToggleSelect: (id: number) => void;
	selectedIds: readonly number[];
	target: "owner" | "rentee";
};

// TODO: 依存している react-beautiful-dnd は deprecated 済みのため `Sortable`（bb-ui）へ移行したい。
// ただし `Sortable` は固定行（FooterRow等）を差し込めないため、現状このtbodyには適用できない。
export function SettlementListRows({
	constructionContext,
	handleMutateSettlements,
	handleUpdate,
	handleDestroy,
	handleUpdateSeq,
	selectedIds,
	onToggleSelect,
	target
}: Props) {
	const { dealSettlements } = constructionContext;

	const [optimisticOrder, setOptimisticOrder] = useState<
		DealSettlement[] | null
	>(null);
	const orderedSettlements = optimisticOrder ?? dealSettlements;

	const renderRow = (
		dealSettlement: DealSettlement,
		index: number
	): ReactNode => (
		<SettlementListRow
			dealSettlement={dealSettlement}
			handleDestroy={handleDestroy}
			handleMutateSettlements={handleMutateSettlements}
			handleUpdate={handleUpdate}
			index={index}
			key={dealSettlement.id}
			onToggleSelect={onToggleSelect}
			selectedIds={selectedIds}
			target={target}
		/>
	);
	const handleDragEnd = async (result: DropResult): Promise<void> => {
		const { destination, source } = result;
		if (!destination) {
			return;
		}
		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		) {
			return;
		}
		const reordered = [...orderedSettlements];
		const [movedSettlement] = reordered.splice(source.index, 1);
		reordered.splice(destination.index, 0, movedSettlement);
		const newSettlements = reordered.map(
			(settlement: DealSettlement, index: number) => ({
				...settlement,
				seq: index + 1
			})
		);
		setOptimisticOrder(newSettlements);
		try {
			await handleUpdateSeq(newSettlements);
			await handleMutateSettlements();
		} finally {
			setOptimisticOrder(null);
		}
	};
	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="droppable">
				{(provided: DroppableProvided): ReactElement<HTMLElement> => (
					<tbody ref={provided.innerRef} {...provided.droppableProps}>
						{orderedSettlements.map(renderRow)}
						{provided.placeholder}
						<SettlementListRestorationAmountRow
							constructionContext={constructionContext}
							target={target}
						/>
						<SettlementListFooterRow
							constructionContext={constructionContext}
							target={target}
						/>
					</tbody>
				)}
			</Droppable>
		</DragDropContext>
	);
}
